/*
 * PDP behaviour for sections/sumitomo-main-product.liquid: gallery swapping, the
 * MOQ/step-aware quantity stepper, Add to Cart vs enquiry, and share.
 *
 * The MOQ and order-step rules come from GSCM.Min_Qty / GSCM.Unit_Qty (metafields.md) and
 * are enforced for real by the cart validation function — this is the inline UX so a buyer
 * is told before checkout bounces them (VALIDATION-RULES-SPEC.md, rule 1 and 2).
 */

if (!customElements.get('sumitomo-product')) {
  customElements.define(
    'sumitomo-product',
    class extends HTMLElement {
      connectedCallback() {
        this.minQty = Math.max(parseInt(this.dataset.minQty, 10) || 1, 1);
        this.step = Math.max(parseInt(this.dataset.unitQty, 10) || 1, 1);

        this.qtyInput = this.querySelector('[data-qty-input]');
        this.qtyError = this.querySelector('[data-qty-error]');
        this.addButton = this.querySelector('[data-add-button]');

        this.querySelector('[data-qty-down]')?.addEventListener('click', () => this.nudge(-1));
        this.querySelector('[data-qty-up]')?.addEventListener('click', () => this.nudge(1));
        this.qtyInput?.addEventListener('change', () => this.validate());
        this.qtyInput?.addEventListener('input', () => this.validate());

        this.querySelectorAll('[data-thumb]').forEach((thumb) =>
          thumb.addEventListener('click', () => this.showImage(thumb)),
        );

        this.querySelector('[data-share-copy]')?.addEventListener('click', () => this.copyLink());

        // Submit through the Cart AJAX API: a native POST sends the buyer to Shopify's
        // own error page for something as ordinary as "only 3 left", losing the page and
        // the quantity they had typed.
        this.form = this.querySelector('form[action*="/cart/add"]');
        this.form?.addEventListener('submit', (e) => this.addToCart(e));

        this.setupStickyBar();

        // Stock is known only once the session layer has revealed the price to a verified
        // buyer. It is never shown (SHIN, 25 Sep); it only picks Add to Cart or enquiry.
        this.addEventListener('sumitomo:price-revealed', (e) => this.showStock(e.detail));
      }

      /** Reveal the order bar once the buy box has scrolled out of view — the design's
       *  behaviour, and the reason it exists: a spec table is long, and the CTA should not
       *  be a scroll away. Falls back to staying hidden where IntersectionObserver is
       *  unavailable, which is safer than a bar that never goes away. */
      setupStickyBar() {
        const bar = this.querySelector('[data-sticky-bar]');
        const anchor = this.querySelector('.sp-buy');
        if (!bar || !anchor || !('IntersectionObserver' in window)) return;

        bar.hidden = false;
        this.stickyObserver = new IntersectionObserver(
          ([entry]) => bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0),
          { threshold: 0 },
        );
        this.stickyObserver.observe(anchor);

        // Proxies the real form so quantity, MOQ validation and the variant all apply.
        bar.querySelector('[data-sticky-add]')?.addEventListener('click', () => {
          this.addButton?.click();
        });
      }

      disconnectedCallback() {
        this.stickyObserver?.disconnect();
      }

      showImage(thumb) {
        const main = this.querySelector('[data-main-image]');
        if (!main) return;
        main.src = thumb.dataset.thumb;
        this.querySelectorAll('[data-thumb]').forEach((t) => t.classList.toggle('is-active', t === thumb));
      }

      showStock(detail) {
        if (!detail || !detail.variant) return;
        // Online ordering off (theme setting): the enquiry block is already showing for every
        // product and there is no Add to Cart for stock to switch on.
        if (this.dataset.ordering === 'off') return;
        // India + Japan quantities for the "more than we have" check; null = not known.
        this.stockQty = detail.stockQty || null;
        this.validate();
        // Every variant is inventoryPolicy DENY (Saiga-san, 18 Sep), so variant.available
        // says whether it can be ordered; an out-of-stock item gets the enquiry route below.
        const orderable = Boolean(detail.variant.available);

        // A variant Shopify will refuse gets the enquiry instead of Add to Cart. The
        // form is hidden rather than the button disabled, so the quantity stepper does not
        // sit there implying the item can still be ordered.
        const form = this.querySelector('form[action*="/cart/add"]');
        const enquiry = this.querySelector('[data-oos-cta]');
        if (form) form.hidden = !orderable;
        if (enquiry) enquiry.hidden = orderable;

        // Same swap on the sticky order bar, or it keeps offering Add to Cart on scroll.
        const stickyAdd = this.querySelector('[data-sticky-add]');
        if (stickyAdd) stickyAdd.hidden = !orderable;
        const stickyEnquiry = this.querySelector('[data-sticky-oos]');
        if (stickyEnquiry) stickyEnquiry.hidden = orderable;
      }

      /** Step by the pack size, never below the minimum order quantity. An off-multiple
       *  value snaps to the nearest valid quantity in the direction pressed: adding a step
       *  to an invalid number keeps it invalid, so the arrow would never clear the error. */
      nudge(direction) {
        if (!this.qtyInput) return;
        const current = parseInt(this.qtyInput.value, 10);
        const min = this.minQty;
        const step = this.step;
        let next;

        if (!Number.isFinite(current) || current < min) {
          next = min;
        } else {
          const steps = (current - min) / step;
          next = Number.isInteger(steps)
            ? current + direction * step
            : min + (direction > 0 ? Math.ceil(steps) : Math.floor(steps)) * step;
        }

        this.qtyInput.value = Math.max(next, min);
        this.validate();
      }

      validate() {
        const added = this.querySelector('[data-added-note]');
        if (added && document.activeElement === this.qtyInput) added.hidden = true;
        if (!this.qtyInput) return true;
        const value = parseInt(this.qtyInput.value, 10);
        let message = '';

        if (!Number.isFinite(value) || value < this.minQty) {
          message = (this.qtyError?.dataset.errMin || 'Minimum order quantity is {min}.').replace('{min}', this.minQty);
        } else if ((value - this.minQty) % this.step !== 0) {
          const nearest = this.minQty + Math.round((value - this.minQty) / this.step) * this.step;
          message = (this.qtyError?.dataset.errStep || 'Sold in multiples of {step} — try {nearest}.')
            .replace('{step}', this.step)
            .replace('{nearest}', Math.max(nearest, this.minQty));
        } else if (this.stockQty && value > this.stockQty.india + this.stockQty.japan) {
          // More than India + Japan together cannot be ordered (Saiga-san, 18 Sep). The
          // message names no figure: stock is not shown to buyers (SHIN, 25 Sep).
          message = this.qtyError?.dataset.errStock || 'This quantity is not available online. Please reduce it or send an enquiry.';
        }

        if (this.qtyError) {
          this.qtyError.textContent = message;
          this.qtyError.hidden = !message;
        }
        if (this.addButton) this.addButton.disabled = Boolean(message);
        return !message;
      }

      async addToCart(event) {
        event.preventDefault();
        if (!this.validate()) return;

        const button = this.addButton;
        const original = button?.textContent;
        if (button) {
          button.disabled = true;
          button.textContent = this.dataset.labelAdding || original;
        }
        this.setFormError('');

        try {
          const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{ id: this.form.querySelector('[name="id"]').value, quantity: Number(this.qtyInput?.value || 1) }],
            }),
          });
          const data = await res.json().catch(() => ({}));

          if (!res.ok) {
            // 422 = Shopify refused the quantity for stock. Its own message names the
            // quantity left, and stock is not shown to buyers (SHIN, 25 Sep).
            const message = res.status === 422
              ? this.qtyError?.dataset.errStock || 'This quantity is not available online. Please reduce it or send an enquiry.'
              : this.dataset.errAddFailed;
            this.setFormError(message);
            return;
          }

          document.dispatchEvent(new CustomEvent('sumitomo:cart-updated', { detail: data }));
          this.showAdded();
        } catch (err) {
          this.setFormError(this.dataset.errOffline);
        } finally {
          if (button) {
            button.disabled = false;
            button.textContent = original;
          }
        }
      }

      /** Requirement 34: a plain "Added to cart · View cart" line above the button. */
      showAdded() {
        const note = this.querySelector('[data-added-note]');
        if (!note) {
          window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
          return;
        }
        note.hidden = false;
        this.refreshCartCount();
      }

      /** The buyer stays on the page now, so the header's cart count must catch up. Dawn only
       *  renders the bubble for a non-empty cart at page load, so it may have to be created. */
      async refreshCartCount() {
        const link = document.getElementById('cart-icon-bubble');
        if (!link) return;
        try {
          const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { headers: { Accept: 'application/json' } })).json();
          let bubble = link.querySelector('.cart-count-bubble');
          if (!bubble) {
            bubble = document.createElement('div');
            bubble.className = 'cart-count-bubble';
            link.appendChild(bubble);
          }
          bubble.innerHTML = cart.item_count < 100 ? `<span aria-hidden="true">${cart.item_count}</span>` : '';
        } catch (e) {
          // The count refreshes on the next page load anyway.
        }
      }

      setFormError(message) {
        if (!this.qtyError) return;
        this.qtyError.textContent = message;
        this.qtyError.hidden = !message;
      }

      async copyLink() {
        try {
          await navigator.clipboard.writeText(window.location.href);
          const done = this.querySelector('[data-share-done]');
          if (done) {
            done.hidden = false;
            setTimeout(() => (done.hidden = true), 2500);
          }
        } catch (e) {
          /* clipboard blocked — the mail link still works */
        }
      }
    },
  );
}
